import { ViolationModel } from 'models';
import { useEffect, useState, useRef } from 'react';
import { FormikContextType, useFormikContext } from 'formik';

const DEFAULT_PROPERTY_PATH = 'global';

/**
 * Transforms the API path object[0].prop to object.0.prop
 * @param path
 */
function transformPath(path: string): string {
  return path.replace(/\[([0-9]*)\]/i, '.$1');
}

/**
 *
 * @param violations array of violations to map to field error
 * @param formik formik context
 * @param defaultPropertyPath used when violation propertyPath is empty
 *
 * @return Orphan violations which could not be attached to any field.
 */
export function useFormikViolations(
  violations: ViolationModel[] | undefined,
  formik: FormikContextType<any>,
  defaultPropertyPath: string = DEFAULT_PROPERTY_PATH,
): ViolationModel[] {
  const { getFieldHelpers, getFieldMeta } = formik;
  const [orphanViolations, setOrphanViolations] = useState<ViolationModel[]>([]);

  const refGetFieldMeta = useRef(getFieldMeta); // To store the ref because its changing whereas it should not

  useEffect(() => {
    const orphanViolations: ViolationModel[] = [];

    violations?.forEach((violation) => {
      const path = transformPath(violation.propertyPath || defaultPropertyPath);
      const helpers = getFieldHelpers(path);
      const { error, value, touched } = refGetFieldMeta.current(path);

      if (undefined === error && undefined === value && !touched) {
        orphanViolations.push(violation);
      } else {
        helpers.setError(violation.message);
      }
    });

    setOrphanViolations(orphanViolations);
  }, [defaultPropertyPath, getFieldHelpers, violations]);

  useEffect(() => {
    refGetFieldMeta.current = getFieldMeta;
  }, [getFieldMeta]);

  return orphanViolations;
}

export type FormikViolationsProps = {
  violations: ViolationModel[] | undefined;
  defaultPropertyPath?: string;
  children?(orphansViolations: ViolationModel[]): any;
};

function FormikViolations({
  violations,
  defaultPropertyPath,
  children,
}: FormikViolationsProps): JSX.Element | null {
  const formik = useFormikContext();

  const orphanViolations = useFormikViolations(violations, formik, defaultPropertyPath);

  if (children instanceof Function) {
    return children(orphanViolations);
  }

  return null;
}

export default FormikViolations;
